import { ajax } from 'jquery';
import { compact, startCase } from 'lodash';
import * as pluralize from 'pluralize';
import * as React from 'react';
import { Column } from 'react-table';
import { useCompanySettings } from '../../javascript/application/useCompanySettings';
import { useCustomEventEffect } from '../shared/hooks/useCustomEventEffect';
import { Table } from '../shared/react_table/table';
import type { ICustomSearch } from '../shared/Search';
import { visit } from '../shared/utilities/turbolinks';
import type { IFilter } from './filters/IFilter';

function ExternalEmpty({ children }: { children: React.ReactNode }) {
  return (
    <div className="external-content-wrap">
      <div className="external-panel__inner">
        <div className="empty-placeholder mar-b-4">
          <div className="empty-placeholder__inner">
            <p className="external-panel__text text-center">{children}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

interface IJobPosting {
  id: string;
  compensation: string;
  compensation_currency: string;
  compensation_frequency: string;
  compensation_maximum: string;
  compensation_minimum: string;
  employment_type: string;
  employment_type_text: string;
  title: string;
  url: string;
  path: string;
  job: {
    department: {
      id: string;
      name: string;
    };
    division?: { id: string; name: string };
    requisition_id?: string;
    structure_custom_group_one?: { id: string; name: string };
  };
  location: {
    id: string;
    city?: string;
    country_code?: string;
    name: string;
    postal_code?: string;
    province: string;
    street_address?: string;
  };
  workplace_type: string;
  workplace_type_text: string;
}

interface IFilterEvent {
  filters: IFilter[];
}

interface IJobPostingRow {
  original: IJobPosting;
}

interface IJobsProps {
  additionalColumns?: { key: string }[];
  pageSize: number;
  showPagination: boolean;
  url: string;
  target: string;
  translations: {
    job_title: string;
    department: string;
    division: string;
    requisition_id: string;
    structure_custom_group_one: string;
    location: string;
    employment_type: string;
    no_positions: string;
    no_opportunities: string;
    workplace_type: string;
  };
  structures: {
    key: string;
    keyPlural: string;
    name: string;
    namePlural: string;
    nameAccessor: string;
  }[];
  visible_columns: {
    employment_type_column_visible: boolean;
    locations_column_visible: boolean;
    departments_column_visible: boolean;
    divisions_column_visible: boolean;
    requisition_id_column_visible: boolean;
    structure_custom_group_ones_column_visible: boolean;
    workplace_type_column_visible: boolean;
    address_column_visible: boolean;
  };
}

export default function Jobs({
  additionalColumns,
  showPagination,
  pageSize,
  url,
  translations,
  target,
  structures,
  visible_columns,
}: IJobsProps) {
  const [loading, setLoading] = React.useState(true);
  const [jobPostings, setJobPostings] = React.useState<IJobPosting[]>([]);
  const [filters, setFilters] = React.useState<IFilter[]>([]);
  const [searchTerm, setSearchTerm] = React.useState<string>('');
  const { isLocationCityStateEnabled, isClientsideJobPostingFilteringEnabled } = useCompanySettings();

  const { dispatch: dispatchReady } = useCustomEventEffect<void>(`${target}:ready`);
  useCustomEventEffect<IFilterEvent>(`${target}:filter`, (event) => setFilters(event.detail.filters));
  useCustomEventEffect<ICustomSearch>(`${target}:search`, (event) => setSearchTerm(event.detail.searchTerm));

  const locationName = (location) => {
    const locationArray = compact([location.city, location.province]);
    if (isLocationCityStateEnabled && locationArray.length > 0) {
      return locationArray.join(', ');
    }
    return location.name;
  };

  React.useEffect(() => {
    if (filters.length > 0) {
      getJobs();
    }
  }, [filters]);

  React.useEffect(() => {
    dispatchReady();
  }, []);

  const transformedFilters = filters.reduce((result, item) => {
    result[item.attribute] = item.values;
    return result;
  }, {});

  function getJobs() {
    setLoading(true);

    ajax({
      cache: false,
      data: transformedFilters,
      dataType: 'json',
      success: (response) => {
        setLoading(false);
        // NOTE (DS): Stop-gap measure to ensure pre-filtered job postings can still be filtered when Fastly incorrectly
        // caches the postings.json endpoint. To be removed once we've ensured query params are properly cached as well for
        // this endpoint.
        isClientsideJobPostingFilteringEnabled ? filterJobPostings(response.data) : setJobPostings(response.data);
      },
      type: 'GET',
      url,
    });
  }

  function filterJobPostings(jobPostings: IJobPosting[]) {
    if (filters.length > 0) {
      const appliedFilters = filters.filter((f) => f.values?.length > 0);
      const filteredJobPostings = jobPostings.filter((jobPosting: IJobPosting) => {
        const divisionFilter = appliedFilters.find((f) => f.attribute === 'division_id');
        if (divisionFilter?.values.length > 0) {
          if (!jobPosting.job.division || !divisionFilter.values.includes(Number(jobPosting.job.division.id))) {
            return false;
          }
        }

        const departmentFilter = appliedFilters.find((f) => f.attribute === 'department_id');
        if (departmentFilter?.values.length > 0) {
          if (!departmentFilter.values.includes(Number(jobPosting.job.department.id))) {
            return false;
          }
        }
        const locationFilter = appliedFilters.find((f) => f.attribute === 'location_id');
        if (locationFilter?.values.length > 0) {
          if (!locationFilter.values.includes(Number(jobPosting.location.id))) {
            return false;
          }
        }
        const structureCustomGroupOneFilter = appliedFilters.find(
          (f) => f.attribute === 'structure_custom_group_one_id',
        );
        if (structureCustomGroupOneFilter?.values.length > 0) {
          if (
            !jobPosting.job.structure_custom_group_one ||
            !structureCustomGroupOneFilter.values.includes(Number(jobPosting.job.structure_custom_group_one.id))
          ) {
            return false;
          }
        }
        return true;
      });
      setJobPostings(filteredJobPostings);
    } else {
      setJobPostings(jobPostings);
    }
  }

  /**
   * Searching here rather than using the in-built searching provided by the table because the table
   * expects data with an attributes key (as its all geared to work with our old way of serializers).
   * The searching within the table won't break anything, it just won't work, so we do it here instead.
   */
  const searchedJobPostings = jobPostings.filter((jobPosting: IJobPosting) => {
    if (!searchTerm) return true;

    const searchableValues = compact([
      jobPosting.title.toLowerCase(),
      jobPosting.job.department.name.toLowerCase(),
      locationName(jobPosting.location).toLowerCase(),
      jobPosting.job.division?.name?.toLowerCase(),
      jobPosting.job.requisition_id?.toLowerCase(),
      jobPosting.job.structure_custom_group_one?.name?.toLowerCase(),
      jobPosting.employment_type_text?.toLowerCase(),
      startCase(jobPosting.employment_type).toLowerCase(),
    ]);
    const lowerSearchTerm = searchTerm.toLowerCase().trim();
    return searchableValues.some((value) => value.includes(lowerSearchTerm));
  });

  const sortedJobs = searchedJobPostings.sort((a, b) => a.title.localeCompare(b.title));

  const orderableColumns: Record<string, Column> = {};

  if (visible_columns.divisions_column_visible) {
    orderableColumns.division = {
      Header: translations.division,
      accessor: 'job.division.name',
      className: 'hide-sm-block col-flex-grow-2',
      headerClassName: 'hide-sm-block col-flex-grow-2',
    };
  }

  if (visible_columns.locations_column_visible) {
    orderableColumns.location = {
      Cell: ({ original: jobPosting }: IJobPostingRow) => {
        return (
          <div title={locationName(jobPosting.location)}>
            <div>{locationName(jobPosting.location)}</div>
          </div>
        );
      },
      Header: translations.location,
      accessor: 'location.name',
      className: 'hide-sm-block col-flex-grow-2',
      headerClassName: 'hide-sm-block col-flex-grow-2',
    };
  }

  if (visible_columns.departments_column_visible) {
    orderableColumns.department = {
      Cell: ({ original: jobPosting }: IJobPostingRow) => (
        <div title={jobPosting.job.department.name}>
          <div>{jobPosting.job.department.name}</div>
        </div>
      ),
      Header: translations.department,
      accessor: 'job.department.name',
      className: 'hide-sm-block col-flex-grow-4',
      headerClassName: 'hide-sm-block col-flex-grow-4',
    };
  }

  if (visible_columns.structure_custom_group_ones_column_visible) {
    orderableColumns.structure_custom_group_one = {
      Header: translations.structure_custom_group_one,
      accessor: 'job.structure_custom_group_one.name',
      className: 'hide-sm-block col-flex-grow-2',
      headerClassName: 'hide-sm-block col-flex-grow-2',
    };
  }

  const orderedColumns = structures.map((structure) => {
    return orderableColumns[structure.key];
  });

  const columns = compact([
    {
      Cell: ({ original: jobPosting }: IJobPostingRow) => {
        const { city, province, postal_code } = jobPosting.location;
        const addressParts = compact([city, province, postal_code]);
        return (
          <div>
            <a href={jobPosting.path} className="hide-sm-block text-bold">
              {jobPosting.title}
            </a>
            <a href={jobPosting.path} className="hide-at-sm-block text-bold">
              <div className="mar-b-1">{jobPosting.title}</div>
              {visible_columns.requisition_id_column_visible && (
                <div className="text-normal text-brand-ash-grey mar-b-1">{jobPosting.job.requisition_id}</div>
              )}
              {visible_columns.departments_column_visible && (
                <div className="text-normal text-brand-ash-grey mar-b-1">{jobPosting.job.department.name}</div>
              )}
              {visible_columns.locations_column_visible && (
                <div className="text-normal text-brand-ash-grey mar-b-1">{locationName(jobPosting.location)}</div>
              )}
              {visible_columns.structure_custom_group_ones_column_visible &&
                jobPosting.job.structure_custom_group_one?.name && (
                  <div className="text-normal text-brand-ash-grey mar-b-1">
                    {jobPosting.job.structure_custom_group_one.name}
                  </div>
                )}
              {additionalColumns.map((column) => {
                return (
                  <div key={column.key} className="text-normal text-brand-ash-grey mar-b-1">
                    {jobPosting[column.key]}
                  </div>
                );
              })}
              {visible_columns.address_column_visible && (
                <div className="text-normal text-brand-ash-grey mar-b-1">{addressParts.join(', ')}</div>
              )}
              {visible_columns.employment_type_column_visible && (
                <div className="text-normal text-brand-ash-grey">{jobPosting.employment_type_text}</div>
              )}
            </a>
          </div>
        );
      },
      Header: translations.job_title,
      accessor: 'title',
      className: 'col-flex-grow-10',
      headerClassName: 'hide-sm-block col-flex-grow-10',
    },
    visible_columns.requisition_id_column_visible && {
      Cell: ({ original: jobPosting }: IJobPostingRow) => {
        return <div>{jobPosting.job.requisition_id}</div>;
      },
      Header: translations.requisition_id,
      accessor: 'requisition_id',
      className: 'hide-sm-block col-flex-grow-2',
      headerClassName: 'hide-sm-block col-flex-grow-2',
    },
    ...orderedColumns,
    ...additionalColumns,
    visible_columns.address_column_visible && {
      Cell: ({ original: jobPosting }: IJobPostingRow) => {
        const { city, province, postal_code } = jobPosting.location;
        const addressParts = compact([city, province, postal_code]);
        return (
          <div title={addressParts.join(', ')}>
            <div>{addressParts.join(', ')}</div>
          </div>
        );
      },
      Header: 'Address',
      accessor: 'location.address',
      className: 'hide-sm-block col-flex-grow-4',
      headerClassName: 'hide-sm-block col-flex-grow-4',
    },
    visible_columns.employment_type_column_visible && {
      Cell: ({ original: jobPosting }: IJobPostingRow) => <div>{jobPosting.employment_type_text}</div>,
      Header: translations.employment_type,
      accessor: 'employment_type',
      className: 'hide-sm-block col-flex-grow-2',
      headerClassName: 'hide-sm-block col-flex-grow-2',
    },
    visible_columns.workplace_type_column_visible && {
      Cell: ({ original: jobPosting }: IJobPostingRow) => <div>{jobPosting.workplace_type_text}</div>,
      Header: translations.workplace_type,
      accessor: 'workplace_type',
      className: 'hide-sm-block col-flex-grow-2',
      headerClassName: 'hide-sm-block col-flex-grow-2',
    },
  ]);

  const isFiltersApplied = filters.some((filter) => filter.values && filter.values.length > 0);

  if (!loading && !isFiltersApplied && jobPostings.length < 1)
    return <ExternalEmpty>{translations.no_positions}</ExternalEmpty>;

  const getTheadTrProps = () => ({ className: 'external-content-wrap' });
  const getTrGroupProps = (_, { original: jobPosting }: IJobPostingRow) => ({ onClick: () => visit(jobPosting.path) });
  const getTrProps = (state, rowInfo) => {
    return {
      className: 'external-content-wrap',
      'data-department': rowInfo.original.job.department.name,
      'data-division': rowInfo.original.job.division?.name,
      'data-location': rowInfo.original.location.name,
      'data-requisition_id': rowInfo.original.job.requisitionId,
      'data-structure-custom-group': rowInfo.original.job.structure_custom_group_one?.name,
    };
  };
  const noDataComponent = () => <ExternalEmpty>{translations.no_opportunities}</ExternalEmpty>;

  return (
    <div>
      {/* This isn't visible to users, but the role status means that when it's changed it is announced to screen readers */}
      <p style={{ fontSize: 0 }} aria-live="polite">
        {pluralize('job', sortedJobs.length, true)} available
      </p>
      <Table
        {...{
          columns,
          getTheadTrProps,
          getTrGroupProps,
          getTrProps,
          loading,
          noDataComponent,
        }}
        className="-themed"
        data={sortedJobs}
        identifier={target}
        showPagination={showPagination}
        pageSize={pageSize}
        resizable={false}
      />
    </div>
  );
}
